import React, { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Tooltip } from '@material-ui/core';
import AccordionCore from 'components/Shared/Accordion/AccordionCore';
import CustomInput from 'components/CustomInput';
import { TenantNameResolver } from 'features/saasManagement/TenantNameResolver/TenantNameResolver';
import { arrayToObject } from 'utils/object-util';
import { LOT_OPTIONS, PRICING_FIELDS, pricingFieldConfig } from './ActivityItemPricingConsts';
import { useActivityItemPricingStepStyles } from './ActivityItemPricingStyles';
import usePrompt from 'hooks/usePrompt';
import { PRICING_MODES } from './PricingCalculator/PricingCalculatorConsts';
import { useSelector } from 'react-redux';
import { selectTenantName } from 'features/user/userSlice';

const PricingItem = ({ item, itemIndex, pricingItems, setPricingItems, onUnsavedChanges, lots, clients, vendors, vehicleTypes, usePricingCalculator = true, requireVendor = true, allowDefaultClient = true, pricingType, materials }) => {
    const classes = useActivityItemPricingStepStyles();
    const { triggerPrompt } = usePrompt();

    const [pricingToEdit, setPricingToEdit] = useState({ ...item });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(item.isNew || false);


    const clientsById = useMemo(() => arrayToObject(clients || []), [clients]);
    const vendorsById = useMemo(() => arrayToObject(vendors || []), [vendors]);
    const lotsById = useMemo(() => arrayToObject(lots || []), [lots]);
    const preferredVendorOptions = useMemo(
        () => (vendors || []).filter(v => (pricingToEdit.vendorIds || []).includes(v.id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [(pricingToEdit.vendorIds || []).join(','), vendors]
    );
    const tenantName = useSelector(selectTenantName);

    const areValuesEqual = (currentValue, nextValue) => {
        if (currentValue === nextValue) return true;
        if (currentValue == null || nextValue == null) return false;
        if (Array.isArray(currentValue) && Array.isArray(nextValue)) {
            return currentValue.length === nextValue.length && currentValue.every((value, index) => value === nextValue[index]);
        }
        if (typeof currentValue === 'object' && typeof nextValue === 'object') {
            const keys1 = Object.keys(currentValue);
            const keys2 = Object.keys(nextValue);
            if (keys1.length !== keys2.length) return false;
            return keys1.every(key => currentValue[key] === nextValue[key]);
        }
        return false;
    };

    const hasPricingChanges = (basePricing, currentPricing) => {
        const ignoredKeys = ['isNew'];
        const keys = Array.from(new Set([
            ...Object.keys(basePricing || {}),
            ...Object.keys(currentPricing || {})
        ])).filter(key => !ignoredKeys.includes(key));

        return keys.some(key => !areValuesEqual(basePricing?.[key], currentPricing?.[key]));
    };

    const setUnsavedChanges = (val) => {
        setHasUnsavedChanges(val);
        onUnsavedChanges?.(val);
    };

    useEffect(() => {
        setPricingToEdit({ ...item });
        setHasUnsavedChanges(item.isNew || false);
        onUnsavedChanges?.(item.isNew || false);
    }, [item]);

    useEffect(() => {
        const currentVendorIds = pricingToEdit.vendorIds || [];
        // Only react to user edits of the vendors field. On mount (and whenever the item
        // prop syncs back into state) the vendor ids match the item's — auto-selecting
        // there would populate Preferred Vendor on items that were saved without one.
        if (currentVendorIds.join(',') === (item.vendorIds || []).join(',')) {
            return;
        }
        if (currentVendorIds.length === 1 && pricingToEdit.preferredVendorId == null) {
            handleChange('preferredVendorId', currentVendorIds[0]);
        } else if (
            pricingToEdit.preferredVendorId != null &&
            !currentVendorIds.includes(pricingToEdit.preferredVendorId)
        ) {
            handleChange('preferredVendorId', null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(pricingToEdit.vendorIds || []).join(',')]);

    useEffect(() => {
        if (!pricingToEdit.clientId && pricingToEdit.preferredVendorId != null) {
            handleChange('preferredVendorId', null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricingToEdit.clientId]);

    const handleChange = (key, value) => {
        setPricingToEdit(prev => {
            if (areValuesEqual(prev[key], value)) {
                return prev;
            }

            const updated = { ...prev, [key]: value };
            setUnsavedChanges(item.isNew || hasPricingChanges(item, updated));
            return updated;
        });
    };

    const handleLotOptionChange = (event) => {
        const newOption = event.target.value
        handleChange('lotOption', newOption);
        let lotIds = [];
        if (newOption === LOT_OPTIONS.ALL_LOTS) {
            lotIds = lots?.map(lot => lot.id) || [];
        } else if (newOption === LOT_OPTIONS.ALL_PMF_LOTS) {
            lotIds = lots?.filter(lot => lot.ownedByTenant).map(lot => lot.id) || [];
        }
        setPricingToEdit(prev => {
            const lotOptionChanged = prev.lotOption !== newOption;
            const lotIdsChanged = !areValuesEqual(prev.selectedLotIds || [], lotIds || []);

            if (!lotOptionChanged && !lotIdsChanged) {
                return prev;
            }

            const updated = { ...prev, lotOption: newOption, selectedLotIds: lotIds };
            setUnsavedChanges(item.isNew || hasPricingChanges(item, updated));
            return updated;
        });
    };

    const updatePricingInParent = (updatedPricing) => {
        if (setPricingItems && itemIndex !== undefined) {
            const updatedItems = [...pricingItems];
            updatedItems[itemIndex] = updatedPricing;
            setPricingItems(updatedItems);
        }
    };

    const removePricingFromParent = () => {
        if (setPricingItems && itemIndex !== undefined) {
            const updatedItems = pricingItems.filter((_, index) => index !== itemIndex);
            setPricingItems(updatedItems);
        }
    };

    const onDone = () => {
        const effectiveIsDefault = isDefaultClientPricing || isAllClientsMode;
        const saved = {
            ...pricingToEdit,
            isNew: false,
            isDefaultClientPricing: effectiveIsDefault,
            // Normalise '' (DropdownFilter empty) and existing null to null for the API.
            clientId: effectiveIsDefault ? null : pricingToEdit.clientId,
        };
        updatePricingInParent(saved);
        setUnsavedChanges(false);
    };

    const onCancelOrRemove = () => {
        if (isDefaultClientPricing && !hasUnsavedChanges) {
            return;
        }

        if (hasUnsavedChanges && item.isNew) {
            setUnsavedChanges(false);
            removePricingFromParent();
        } else if (hasUnsavedChanges) {
            setPricingToEdit({ ...item });
            setUnsavedChanges(false);
        } else {
            triggerPrompt({
                title: "Remove Item",
                content: "Are you sure you want to remove this pricing?",
                onConfirm: () => {
                    removePricingFromParent();
                },
                onCancel: () => { },
            });
        }
    };

    const lotOption = pricingToEdit.lotOption || LOT_OPTIONS.ALL_LOTS;
    const selectedLotIds = pricingToEdit.selectedLotIds || [];
    // The toggle must be on whenever at least one selected lot is a non-PMF lot
    // (a lot not owned by the tenant). Otherwise editing a bundle that already
    // includes such a lot would render the toggle off and hide it from the picker.
    const hasNonPmfSelectedLot = selectedLotIds.some(id => lotsById[id] && !lotsById[id].ownedByTenant);
    const includeNonPmfLots = pricingToEdit.includeNonPmfLots || hasNonPmfSelectedLot;
    const clientId = pricingToEdit.clientId;
    // Bundles never use the "all clients" default/blank-client mode — a client is always required there.
    const isDefaultClientPricing = allowDefaultClient && Boolean(item.isDefaultClientPricing || item.clientId === null);
    // No client selected — covers both "never chosen" (undefined) and "explicitly cleared" ('').
    // Both states behave identically: vendor cost only, mode locked to Cost-Plus.
    const isAllClientsMode = allowDefaultClient && !isDefaultClientPricing && !clientId;
    const vendorIds = pricingToEdit.vendorIds || [];
    const vehicleTypeIdsList = pricingToEdit.vehicleTypeIdsList || [];
    const preferredVendorId = pricingToEdit.preferredVendorId ?? null;
    const priceConfig = pricingToEdit.priceConfig || { priceMode: PRICING_MODES.MARGIN.id, cost: null, margin: null, price: null };
    const simplePrice = pricingToEdit.price ?? null;

    const savedClientName = isDefaultClientPricing ? 'All clients' : clientsById[item.clientId]?.name;
    const savedVendorNames = (item.vendorIds || []).map(id => vendorsById[id]?.name).filter(Boolean);

    const savedLotOption = item.lotOption || LOT_OPTIONS.ALL_LOTS;
    let savedLotSummary;
    if (savedLotOption === LOT_OPTIONS.ALL_LOTS) {
        savedLotSummary = 'All lots';
    } else if (savedLotOption === LOT_OPTIONS.ALL_PMF_LOTS || savedLotOption === LOT_OPTIONS.ALL_TENANT_LOTS) {
        savedLotSummary = `All ${tenantName} lots`;
    } else if (savedLotOption === LOT_OPTIONS.SPECIFIC_LOTS) {
        const lotNames = (item.selectedLotIds || []).map(id => lotsById[id]?.name).filter(Boolean);
        savedLotSummary = lotNames.length > 0 ? lotNames.join(', ') : 'Specific lots';
    }

    let headerText = 'New Pricing';
    if (requireVendor && savedClientName && savedVendorNames.length > 0) {
        headerText = `${savedClientName} - ${savedVendorNames.join(', ')}`;
    } else if (savedClientName) {
        headerText = savedClientName;
    } else if (requireVendor && savedVendorNames.length > 0) {
        headerText = savedVendorNames.join(', ');
    }

    const hasSavedInfo = Boolean(savedClientName || savedVendorNames.length > 0);
    if (hasSavedInfo && savedLotSummary) {
        headerText = `${headerText} - ${savedLotSummary}`;
    }

    // Client is required unless the user has explicitly cleared it (isAllClientsMode)
    // or it is an auto-generated default (isDefaultClientPricing).
    const clientMissing = !isDefaultClientPricing && !isAllClientsMode && !clientId;

    const isDoneDisabled =
        !hasUnsavedChanges
        || clientMissing
        || (requireVendor && !vendorIds.length)
        || (lotOption === LOT_OPTIONS.SPECIFIC_LOTS && !selectedLotIds.length)
        || (!usePricingCalculator && (simplePrice == null || Number.isNaN(simplePrice)));

    const getDoneTooltip = () => {
        if (!hasUnsavedChanges) return 'No changes to save';
        if (clientMissing) return 'Please select a client or leave blank to apply to all clients';
        if (requireVendor && !vendorIds.length) return 'Please select at least one vendor';
        if (lotOption === LOT_OPTIONS.SPECIFIC_LOTS && !selectedLotIds.length) return 'Please select at least one lot';
        if (!usePricingCalculator && (simplePrice == null || Number.isNaN(simplePrice))) return 'Please enter a valid price';
        return '';
    };

    const readOnlyFieldTooltip = 'Default pricing applies to all clients — the client cannot be changed here.';
    const renderWithReadOnlyTooltip = (content) => (
        isDefaultClientPricing ? (
            <Tooltip title={readOnlyFieldTooltip} arrow>
                <div className={classes.readOnlyTooltipWrap}><div className={classes.readOnlyField}>{content}</div></div>
            </Tooltip>
        ) : content
    );

    return (
        <AccordionCore
            headerText={headerText}
            open={item.isNew}
            className={isDefaultClientPricing ? classes.defaultAccordion : undefined}
            headerActions={isDefaultClientPricing ? (
                <span className={classes.defaultBadge}>Default</span>
            ) : null}
        >
            <div
                className={classes.container}
            >
                <div className={classes.section}>
                    <div className={classes.stepHeader}>
                        <span className={classes.stepNumber}>1</span>
                        <h4>{requireVendor ? 'Select Client and Vendors' : 'Select Client'}</h4>
                    </div>
                    <Grid container spacing={2}>
                        <Grid item xs={requireVendor ? 6 : 12}>
                            {isDefaultClientPricing ? renderWithReadOnlyTooltip(
                                <div className={classes.disabledInputOverride}>
                                    <CustomInput
                                        id="pricing-item-client-default"
                                        label="Client"
                                        value="All clients"
                                        elementType="input"
                                        disabled
                                    />
                                </div>
                            ) : (
                                <>
                                    {pricingFieldConfig[PRICING_FIELDS.CLIENT]?.component(
                                        clientId,
                                        clients || [],
                                        (val) => handleChange('clientId', val)
                                    )}
                                    {allowDefaultClient && !clientId && (
                                        <span className={classes.allClientsHelperText}>
                                            Leave blank to apply to all clients — vendor cost only
                                        </span>
                                    )}
                                </>
                            )}
                        </Grid>
                        {requireVendor && (
                            <Grid item xs={6}>
                                {pricingFieldConfig[PRICING_FIELDS.VENDOR]?.component(
                                    vendorIds,
                                    vendors || [],
                                    (val) => handleChange('vendorIds', val)
                                )}
                            </Grid>
                        )}
                        {requireVendor && clientId && (
                            <Grid item xs={6}>
                                {pricingFieldConfig[PRICING_FIELDS.PREFERRED_VENDOR]?.component(
                                    preferredVendorId,
                                    preferredVendorOptions,
                                    (val) => handleChange('preferredVendorId', val)
                                )}
                            </Grid>
                        )}
                    </Grid>
                </div>

                <div className={classes.section}>
                    <div className={classes.stepHeader}>
                        <span className={classes.stepNumber}>2</span>
                        <h4>Select applicable Lots</h4>
                    </div>
                    {pricingFieldConfig[PRICING_FIELDS.LOT_OPTIONS]?.component(
                        lotOption,
                        handleLotOptionChange
                    )}
                    {lotOption === LOT_OPTIONS.SPECIFIC_LOTS && (
                        <>
                            <div className={classes.lotToggleSection}>
                                {pricingFieldConfig[PRICING_FIELDS.LOT_TOGGLE]?.component(
                                    includeNonPmfLots,
                                    (event) => handleChange('includeNonPmfLots', event.target.checked),
                                    <TenantNameResolver startText="Include Non" endText="Lots" />
                                )}
                            </div>
                            <div className={classes.lotsSection}>
                                {pricingFieldConfig[PRICING_FIELDS.LOTS]?.component(
                                    selectedLotIds,
                                    lots,
                                    (val) => handleChange('selectedLotIds', val),
                                    !includeNonPmfLots,
                                    clientId
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className={classes.section}>
                    <div className={classes.stepHeader}>
                        <span className={classes.stepNumber}>3</span>
                        <h4>Select Vehicle Types</h4>
                    </div>
                    {pricingFieldConfig[PRICING_FIELDS.VEHICLE_TYPES]?.component(
                        vehicleTypeIdsList,
                        vehicleTypes || [],
                        (val) => handleChange('vehicleTypeIdsList', val)
                    )}
                </div>

                <div className={classes.section}>
                    <div className={classes.stepHeader}>
                        <span className={classes.stepNumber}>4</span>
                        <h4>Pricing</h4>
                    </div>
                    {usePricingCalculator ? (
                        pricingFieldConfig[PRICING_FIELDS.PRICE]?.component(
                            clientId,
                            priceConfig,
                            (val) => handleChange('priceConfig', val),
                            pricingType,
                            materials,
                            isDefaultClientPricing || isAllClientsMode
                        )
                    ) : (
                        <CustomInput
                            id="pricing-item-price"
                            label="Price"
                            value={simplePrice ?? ''}
                            onChange={(val) => handleChange('price', val === '' ? null : parseFloat(val))}
                            elementType="input"
                            numberOnly
                            required
                        />
                    )}
                </div>

                <Grid container justifyContent="flex-end" spacing={1}>
                    <Grid item>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onCancelOrRemove}
                            disabled={isDefaultClientPricing && !hasUnsavedChanges}
                        >
                            {hasUnsavedChanges ? 'Cancel' : 'Remove Item'}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Tooltip title={getDoneTooltip()} arrow disableHoverListener={!isDoneDisabled}>
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onDone}
                                    disabled={isDoneDisabled}
                                >
                                    Done
                                </Button>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </div>
        </AccordionCore>
    );
};

export default PricingItem;
